/**
 * Element pick and region drag. Techniques adapted from RedLine
 * src/overlay.js:574-608 (element pick), 734-772 (region drag) and 779-795 (Esc).
 * Both ignore the feedback panel's own DOM.
 */
import { resolveElement } from './resolveElement';
import type { ElementCapture, RegionCapture } from './types';

const OWN_DOM = '[data-feedback-panel],[data-feedback-pill],[data-feedback-picking-banner],[data-feedback-highlight]';
const MIN_REGION = 6;

export interface PickOptions {
  document?: Document;
  onPickStart?: () => void;
  onPickEnd?: () => void;
}

export interface PickHandle<T> {
  cancel(): void;
  result: Promise<T | null>;
}

let active: { cancel(): void } | null = null;

const isOwn = (t: EventTarget | null): boolean =>
  t instanceof Element && t.closest(OWN_DOM) !== null;

function makeBox(doc: Document, dashed: boolean): HTMLDivElement {
  const box = doc.createElement('div');
  box.dataset.feedbackHighlight = '';
  Object.assign(box.style, {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: '45',
    border: `2px ${dashed ? 'dashed' : 'solid'} var(--color-primary)`,
    background: 'color-mix(in srgb, var(--color-primary) 12%, transparent)',
    display: 'none',
  });
  doc.body.appendChild(box);
  return box;
}

function start<T>(
  opts: PickOptions,
  dashed: boolean,
  attach: (
    doc: Document,
    box: HTMLDivElement,
    finish: (v: T | null) => void,
  ) => Array<[string, (e: Event) => void]>,
): PickHandle<T> {
  active?.cancel();
  const doc = opts.document ?? document;
  const win = doc.defaultView ?? globalThis.window;
  const box = makeBox(doc, dashed);
  let done = false;
  let resolve!: (v: T | null) => void;
  const result = new Promise<T | null>((r) => {
    resolve = r;
  });
  const listeners: Array<[Target, string, (e: Event) => void]> = [];
  type Target = Document | Window;
  const finish = (v: T | null): void => {
    if (done) return;
    done = true;
    try {
      for (const [t, name, fn] of listeners) t.removeEventListener(name, fn, true);
      box.remove();
    } finally {
      if (active === handle) active = null;
      try {
        opts.onPickEnd?.();
      } finally {
        resolve(v);
      }
    }
  };
  const handle: PickHandle<T> = { cancel: () => finish(null), result };
  active = handle;
  for (const [name, fn] of attach(doc, box, finish)) listeners.push([doc, name, fn]);
  const onKey = (e: Event): void => {
    if ((e as KeyboardEvent).key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      finish(null);
    }
  };
  listeners.push([win, 'keydown', onKey]);
  for (const [t, name, fn] of listeners) t.addEventListener(name, fn, true);
  opts.onPickStart?.();
  return handle;
}

export function startElementPick(opts: PickOptions = {}): PickHandle<ElementCapture> {
  return start<ElementCapture>(opts, false, (doc, box, finish) => {
    let target: Element | null = null;
    const onMove = (e: Event): void => {
      const m = e as MouseEvent;
      const el = doc.elementFromPoint(m.clientX, m.clientY);
      if (!el || isOwn(el)) {
        target = null;
        box.style.display = 'none';
        return;
      }
      target = el;
      const r = el.getBoundingClientRect();
      Object.assign(box.style, {
        display: 'block',
        left: `${r.left}px`,
        top: `${r.top}px`,
        width: `${r.width}px`,
        height: `${r.height}px`,
      });
    };
    const onClick = (e: Event): void => {
      if (isOwn(e.target)) return;
      const el = target ?? (e.target instanceof Element ? e.target : null);
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      finish(resolveElement(el));
    };
    return [
      ['mousemove', onMove],
      ['click', onClick],
    ];
  });
}

export function startRegionPick(opts: PickOptions = {}): PickHandle<RegionCapture> {
  return start<RegionCapture>(opts, true, (doc, box, finish) => {
    const win = doc.defaultView ?? globalThis.window;
    let origin: { x: number; y: number } | null = null;
    const rectOf = (e: MouseEvent) => {
      const o = origin as { x: number; y: number };
      return {
        left: Math.min(o.x, e.clientX),
        top: Math.min(o.y, e.clientY),
        width: Math.abs(e.clientX - o.x),
        height: Math.abs(e.clientY - o.y),
      };
    };
    const onDown = (e: Event): void => {
      if (isOwn(e.target)) return;
      const m = e as MouseEvent;
      e.preventDefault();
      origin = { x: m.clientX, y: m.clientY };
    };
    const onMove = (e: Event): void => {
      if (!origin) return;
      const r = rectOf(e as MouseEvent);
      Object.assign(box.style, {
        display: 'block',
        left: `${r.left}px`,
        top: `${r.top}px`,
        width: `${r.width}px`,
        height: `${r.height}px`,
      });
    };
    const onUp = (e: Event): void => {
      if (!origin) return;
      const r = rectOf(e as MouseEvent);
      origin = null;
      if (r.width < MIN_REGION || r.height < MIN_REGION) {
        finish(null);
        return;
      }
      finish({ ...r, scrollX: win.scrollX, scrollY: win.scrollY });
    };
    return [
      ['pointerdown', onDown],
      ['pointermove', onMove],
      ['pointerup', onUp],
    ];
  });
}
